import { useQuery, useQueryClient, queryOptions } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import {
	fetchAgentRun,
	fetchAgentRunActions,
	fetchAgentRunEvents,
	fetchAgentRunPreview,
	fetchDraft,
	fetchEstimate,
} from '../api/agentRunsApi';
import { isWorking } from '../constants/conversation';
import { mergeRunEvents } from '../utils/buildConversation';

import { agentRunKeys } from './keys';

const WORKING_POLL_MS = 10_000;

/**
 * The user's draft conversation. Polls while the assistant works, so the "Neue Suche" badge
 * and the job strip stay current with the popup closed (no stream without a visible popup).
 */
export const agentDraftOptions = () =>
	queryOptions({
		queryKey: agentRunKeys.draft(),
		queryFn: async () => (await fetchDraft()).run,
		staleTime: STALE_TIMES.FREQUENT,
		refetchInterval: (query) => (isWorking(query.state.data?.status) ? WORKING_POLL_MS : false),
	});

export function useAgentDraft() {
	return useQuery(agentDraftOptions());
}

/**
 * One conversation's snapshot. With `poll`, it refetches while the assistant works, for a
 * conversation watched without its stream (the popup closed or showing the search mask).
 */
export const agentRunOptions = (runId, { poll = false } = {}) =>
	queryOptions({
		queryKey: agentRunKeys.detail(runId),
		queryFn: () => fetchAgentRun(runId),
		enabled: Boolean(runId),
		staleTime: STALE_TIMES.FREQUENT,
		refetchInterval: (query) => (poll && isWorking(query.state.data?.status) ? WORKING_POLL_MS : false),
	});

export function useAgentRun(runId, options) {
	return useQuery(agentRunOptions(runId, options));
}

/**
 * The stored events of a conversation. Every fetch is merged by sequence number into what the
 * cache already holds, so a replay that lands after a live event never drops it; a mount always
 * refetches, so a conversation that moved on while the popup was closed gets its missing tail.
 */
export function useAgentRunEvents(runId, enabled = true) {
	const qc = useQueryClient();
	return useQuery({
		queryKey: agentRunKeys.events(runId),
		queryFn: async () => {
			const fetched = (await fetchAgentRunEvents(runId)).events;
			return mergeRunEvents(qc.getQueryData(agentRunKeys.events(runId)), fetched);
		},
		enabled: Boolean(runId) && enabled,
		staleTime: STALE_TIMES.STATIC,
		refetchOnMount: 'always',
	});
}

export function useAgentRunPreview(runId, enabled) {
	return useQuery({
		queryKey: agentRunKeys.preview(runId),
		queryFn: async () => (await fetchAgentRunPreview(runId)).companies,
		enabled: Boolean(runId) && enabled,
		staleTime: STALE_TIMES.STABLE,
	});
}

export function useAgentRunEstimate(runId, enabled) {
	return useQuery({
		queryKey: agentRunKeys.estimate(runId),
		queryFn: () => fetchEstimate(runId),
		enabled: Boolean(runId) && enabled,
		staleTime: STALE_TIMES.FREQUENT,
	});
}

export function useAgentRunActions(runId, enabled) {
	return useQuery({
		queryKey: agentRunKeys.actions(runId),
		queryFn: async () => (await fetchAgentRunActions(runId)).actions,
		enabled: Boolean(runId) && enabled,
		staleTime: STALE_TIMES.FREQUENT,
	});
}
